import { buildMessageTypeFields, ChatMessageType } from "./messageTypes";

/**
 * Servy's langCode only accepts these values (the server rejects anything else
 * with "langCode must be one of the following values: …"). The app's own
 * language codes map onto them; any other language falls back to English.
 */
const SERVY_LANG_CODES = {
  EN: "en",
  AR: "ar",
  FR: "fr",
  RU: "ru",
  HI: "hi",
  DE: "de",
  ES: "es",
  PT: "pt",
  NL: "nl",
  CH: "zh-Hans",
  "CH-HANS": "zh-Hans",
  "CH-HANT": "zh-Hans",
  "ZH-HANS": "zh-Hans",
};

export const toServyLangCode = (appLang) =>
  SERVY_LANG_CODES[String(appLang || "").toUpperCase()] || "en";

/**
 * A Servy question is a normal client:sendPrivateMessage with the existing
 * chat context, flagged with isAIQuestion plus the question language.
 * room_uuid must match qrCode: both are chat_uuid from the qr-code API.
 */
export const buildServyQuestionPayload = ({
  qrCode,
  unitName,
  hostId,
  text,
  langCode,
}) => ({
  qrCode,
  unitName: unitName || "",
  receiverId: String(hostId),
  ...buildMessageTypeFields(ChatMessageType.TEXT, { text }),
  isAIQuestion: true,
  room_uuid: qrCode,
  langCode,
});

/**
 * Ack of client:sendPrivateMessage. Unlike the host chat's parser, any 4xx/5xx
 * status counts as a failure — the server answers an unknown user with 404.
 */
export const parseServyAck = (response) => {
  if (response == null || typeof response !== "object") {
    return { ok: true, body: null };
  }

  const status = Number(response.status);
  const failed =
    (Number.isFinite(status) && status >= 400) ||
    response.success === false ||
    Boolean(response.error) ||
    response.message === "exception";
  if (failed) {
    return {
      ok: false,
      body: null,
      error:
        typeof response.message === "string" ? response.message : "send_failed",
    };
  }

  const body =
    response.body && typeof response.body === "object"
      ? response.body
      : response.data && typeof response.data === "object"
        ? response.data
        : null;
  return { ok: true, body };
};

const isObject = (value) => value != null && typeof value === "object";

/** Live chat payloads arrive bare or wrapped, the same shapes the host chat accepts. */
export const unwrapIncomingMessages = (payload) => {
  const candidate =
    payload?.body?.messages ??
    (isObject(payload?.message) ? payload.message : null) ??
    (isObject(payload?.data) ? payload.data : null) ??
    (isObject(payload?.body) ? payload.body : null) ??
    payload;

  if (Array.isArray(candidate)) return candidate.filter(isObject);
  if (Array.isArray(candidate?.messages)) return candidate.messages.filter(isObject);
  return isObject(candidate) ? [candidate] : [];
};

/*
 * Servy's answer arrives on newPrivateMessage like any chat message, sent with
 * the host's id but flagged isAIAnswer. Example (staging, 2026-10-05):
 *   { senderId: "2", receiverId: <guestId>, messageType: "text",
 *     isAIQuestion: false, isAIAnswer: true, _id, text, createdAt, … }
 */
export const isServyAnswer = (message) =>
  isObject(message) && message.isAIAnswer === true;

export const getServyAnswerText = (message) =>
  typeof message?.text === "string" ? message.text.trim() : "";

/**
 * Suggested follow-up questions under an answer. The answer payload has no
 * suggestions field yet, so there's nothing to show; wire it here once the
 * backend sends one.
 */
export const getServyFollowUps = () => [];

export const getServyMessageId = (message) => {
  const id =
    message?._id ?? message?.id ?? message?.messageId ?? message?.message_id;
  return id != null ? String(id) : null;
};

/**
 * Servy's own conversation is loaded with the host chat's history event plus
 * isAI: true; without the flag the server returns the host conversation.
 */
export const buildServyHistoryPayload = ({ qrCode, unitName, hostId }) => ({
  qrCode,
  unitName: unitName || "",
  receiverId: String(hostId),
  isAI: true,
});

/** History ack, in the shape the host chat's history uses: { status, body: { messages } }. */
export const parseServyHistory = (response) => {
  if (Array.isArray(response)) return { ok: true, messages: response };
  if (!isObject(response)) return { ok: false, messages: [] };

  const status = response.status;
  const ok =
    status == null ||
    status === 200 ||
    status === "OK" ||
    status === "ok";
  const body = isObject(response.body)
    ? response.body
    : isObject(response.data)
      ? response.data
      : response;
  const list = Array.isArray(body?.messages)
    ? body.messages
    : Array.isArray(response.messages)
      ? response.messages
      : Array.isArray(body)
        ? body
        : [];
  return { ok, messages: list.filter(isObject) };
};

const messageTime = (message) =>
  Date.parse(message?.timestamp || message?.createdAt || message?.created_at) ||
  0;

const senderIdOf = (message) =>
  message?.sender?.id ?? message?.senderId ?? message?.sender_id ?? null;

/**
 * Earlier Servy questions and answers, oldest first, as the chat screen shows
 * them. History items carry no isAI flags — only `sender.id` — but the isAI
 * history holds nothing else, so the guest's own messages are the questions
 * and every other message is Servy's answer:
 *   { _id, text, timestamp, sender: { id: <guestId | hostId>, name } }
 */
export const toServyConversation = (history, guestId) =>
  [...history]
    .sort((a, b) => messageTime(a) - messageTime(b))
    .map((message) => {
      const text = getServyAnswerText(message);
      if (!text) return null;
      const id =
        getServyMessageId(message) || `servy-history-${messageTime(message)}`;
      const sender = senderIdOf(message);
      const fromGuest =
        message.isAIQuestion === true ||
        (sender != null && guestId != null && String(sender) === String(guestId));
      return isServyAnswer(message) || !fromGuest
        ? {
            id,
            role: "servy",
            text,
            followUps: getServyFollowUps(message),
            status: "received",
          }
        : { id, role: "guest", text, status: "answered" };
    })
    .filter(Boolean);

/**
 * The loaded history, then whatever is shown that it doesn't already hold.
 * Answers arrive live under their server id, so those match by id; a question
 * is shown under a client-made id, so its server copy is matched by text,
 * once each. What's left (a question still waiting, an answer newer than the
 * history) stays after the history, in the order shown.
 */
export const mergeServyHistory = (current, earlier) => {
  const earlierIds = new Set(earlier.map((message) => message.id));
  const unclaimed = earlier.filter(
    (message) =>
      message.role === "guest" &&
      !current.some((shown) => shown.id === message.id),
  );

  const remaining = current.filter((message) => {
    if (earlierIds.has(message.id)) return false;
    if (message.role !== "guest") return true;
    const copy = unclaimed.findIndex((held) => held.text === message.text);
    if (copy === -1) return true;
    unclaimed.splice(copy, 1);
    return false;
  });

  return [...earlier, ...remaining];
};
